import { AsyncPipe, CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { DeviceService } from '@shared/services';
import { WindowRef } from '@spartacus/core';
import { Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import Swiper from 'swiper';
import { register } from 'swiper/element/bundle';
import { SwiperOptions } from 'swiper/types';
import { NamedTemplateDirective } from '../../directives/named-template';
import { SwiperComponentService } from './swiper-component.service';

register();

@Component({
  selector: 'sd-swiper',
  templateUrl: './swiper.component.html',
  styleUrls: ['./swiper.component.scss'],
  imports: [AsyncPipe, NgTemplateOutlet, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [SwiperComponentService],
})
export class SdSwiperComponent implements OnInit, AfterViewInit, OnDestroy {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('items')
  set _items(data: any[] | null) {
    this.items = data
      ? data.map((item: any) => {
          return item instanceof Observable ? item : of(item);
        })
      : [];
  }

  @ViewChild('buttonPrev', { static: true }) buttonPrevElRef: ElementRef;
  @ViewChild('buttonNext', { static: true }) buttonNextElRef: ElementRef;
  @ViewChild('pagination', { static: true }) paginationElRef: ElementRef;

  @ContentChild(TemplateRef, { static: true }) template: TemplateRef<any>;
  @ContentChildren(NamedTemplateDirective) templates: QueryList<NamedTemplateDirective<string>>;

  get temlateByName(): Record<'ssrPlaceholder', NamedTemplateDirective<string>> {
    return this.templates?.reduce(
      (acc, template: NamedTemplateDirective<string>) => ({
        ...acc,
        [template.name]: template,
      }),
      {} as Record<'ssrPlaceholder', NamedTemplateDirective<string>>,
    );
  }

  childElements: QueryList<ElementRef>;

  @HostBinding('style') swiperStyles: string;
  // после гидратации элементы свайпера дублируются,
  // поэтому отключаем ее для этого компонента
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('config')
  set _config(config: SwiperOptions) {
    this.config = config;
    this.swiperComponentService.updateConfig(config);
  }

  @Output() activeIndexChange = new EventEmitter<number>();
  @Output() slidesPerViewChange = new EventEmitter<number>();
  @Output() reachEnd = new EventEmitter<Swiper>();
  @Output() fromEdge = new EventEmitter<Swiper>();
  @Output() swiperInit = new EventEmitter<Swiper>();

  /**
   * Конфиг из инпута
   */
  config: SwiperOptions = {};
  /**
   * Конфиг, обогащенный значениями по умолчанию
   */
  enreachedConfig: SwiperOptions = {};

  swiperSlidesConfig$: Observable<{ slidesPerView: number | 'auto'; gridRows: number }>;
  items: Observable<any>[] = [];

  get isShownPlaceholder(): boolean {
    return this.isServer && Boolean(this.temlateByName.ssrPlaceholder);
  }

  isServer = !this.windowRef.isBrowser();

  protected subscription = new Subscription();
  private swiperComponentService: SwiperComponentService = inject(SwiperComponentService);
  private elRef: ElementRef = inject(ElementRef);

  constructor(
    private deviceService: DeviceService,
    private cd: ChangeDetectorRef,
    private windowRef: WindowRef,
    @Inject(PLATFORM_ID) protected platform: any,
  ) {}

  ngOnInit(): void {
    this.enreachedConfig = {
      loop: this.config.loop ?? false,
      initialSlide: this.config.initialSlide ?? 0,
      resizeObserver: true,
      scrollbar: false,
      watchOverflow: true,
      touchStartPreventDefault: false,
      injectStyles: this.injectCustomSwiperStyles(),
      pagination: this.config.pagination ?? {
        clickable: true,
        type: 'bullets',
      },
      navigation: this.config.navigation ?? true,
      // конфиг из инпута приоритетнее
      ...this.config,
      on: {
        activeIndexChange: (swiper: Swiper): void => {
          this.activeIndexChange.emit(swiper.activeIndex);
        },
        reachEnd: (swiper): void => {
          this.reachEnd.emit(swiper);
        },
        fromEdge: (swiper): void => {
          this.fromEdge.emit(swiper);
        },
      },
    };

    this.subscription.add(
      this.deviceService.isMobileXsDown$.subscribe((isMobile) => {
        // если нет управления навигацией из пэрент компонента, то управляем тут
        if (this.config.navigation === undefined) {
          this.enreachedConfig.navigation = !isMobile;
          this.cd.markForCheck();
        }
      }),
    );

    this.swiperSlidesConfig$ = this.deviceService.breakpoint$.pipe(
      map((breakpoint) => {
        const width = this.windowRef.nativeWindow?.innerWidth || this.deviceService.getSize(breakpoint) - 1;
        let slidesPerView = this.enreachedConfig.slidesPerView;
        let gridRows = this.enreachedConfig.grid?.rows || 1;

        if (width && this.enreachedConfig.breakpoints) {
          // работает, только если брейкпойнты указаны числами
          const sizesAscending = Object.keys(this.enreachedConfig.breakpoints).sort(
            (a: string, b: string) => Number(a) - Number(b),
          );

          for (const size of sizesAscending) {
            if (width >= Number(size)) {
              const config = this.enreachedConfig.breakpoints[size];
              if (config.slidesPerView) {
                slidesPerView = config.slidesPerView;
              }
              if (config.grid?.rows) {
                gridRows = config.grid.rows;
              }
            }
          }
        }

        return { slidesPerView, gridRows };
      }),
    );

    this.subscription.add(
      this.swiperSlidesConfig$
        .pipe(
          map(({ slidesPerView }) => slidesPerView),
          distinctUntilChanged(),
        )
        .subscribe((slidesPerView) => {
          this.slidesPerViewChange.emit(slidesPerView as number);
        }),
    );

    this.subscription.add(
      this.swiperSlidesConfig$.subscribe((swiperSlidesConfig) => {
        this.swiperStyles = this.getSwiperStyles(swiperSlidesConfig);
      }),
    );
  }

  ngAfterViewInit(): void {
    if (!this.isShownPlaceholder && !this.isServer) {
      const swiper = this.swiperComponentService.initNewSwiperInstance(this.elRef, this.enreachedConfig);
      this.swiperInit.emit(swiper);
    }
  }

  ngOnDestroy(): void {
    this.swiperComponentService.destroySwiperInstance();
    this.subscription.unsubscribe();
  }

  getSwiperStyles(swiperSlidesConfig: { slidesPerView: number | 'auto'; gridRows: number }): string {
    const { slidesPerView, gridRows } = swiperSlidesConfig;
    const styles = {
      '--swiper-space-between': (this.enreachedConfig.spaceBetween || 0) + 'px',
      '--swiper-flex-wrap': slidesPerView === 1 && gridRows > 1 ? 'wrap' : 'unset',
    };

    return Object.entries(styles)
      .map(([name, value]) => `${name}: ${value};`)
      .join(' ');
  }

  getSlideClasses(swiperSlidesConfig: { slidesPerView: number | 'auto'; gridRows: number }, index: number): string {
    const classes = [];
    const { slidesPerView, gridRows } = swiperSlidesConfig;
    if (gridRows === 1) {
      if ((slidesPerView as number) <= index) {
        classes.push('item-wrapper-hidden');
      } else if (index !== 0) {
        classes.push('item-wrapper-space-left');
      }
    }

    if (slidesPerView === 1 && gridRows > 1) {
      if (index % Math.ceil(this.items.length / gridRows) !== 0) {
        classes.push('item-wrapper-hidden');
      } else if (index !== 0) {
        classes.push('item-wrapper-space-top');
      }
    }

    return classes.join(' ');
  }

  // стили которые нельзя переопределить через css part
  private injectCustomSwiperStyles(): string[] | undefined {
    return [
      // прячем не нужные svg иконки
      `
      .swiper-button-prev svg {
        display: none;
      }
      .swiper-button-next svg {
        display: none;
      }
    `,
      // неактивные кнопки навигации прячем
      `
     .swiper-button-disabled {
        display: none !important;
      }
    `,
      // стили для SSR рендеринга, чтобы не было мигания и прочих дерганий после загрузки страницы
      `
      .swiper-container:not(.swiper-container-initialized) .swiper-wrapper {
        flex-wrap: var(--swiper-flex-wrap);
      }
      .swiper-container:not(.swiper-container-initialized)  .item-wrapper {
        width: 100%;
      }
      .swiper-container:not(.swiper-container-initialized) .item-wrapper-space-left {
        margin-left: var(--swiper-space-between);
      }
      .swiper-container:not(.swiper-container-initialized) .item-wrapper-space-top {
        margin-top: var(--swiper-space-between);
      }
      .swiper-container:not(.swiper-container-initialized) .item-wrapper-hidden {
        display: none;
      }
    `,
    ];
  }
}
